'use client';

import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { HUBSPOT_MARKETPLACE_INSTALL_PATH } from '@/lib/hubspot-marketplace-install';
import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { GoogleTagManager } from '@next/third-parties/google';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { useAuth } from '@/contexts/AuthProvider';
import { useUserContext } from '@/contexts/UserProvider';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import {
  hasAnalyticsConsent,
  subscribeConsent,
} from '@/lib/analytics/consent';
import { flushPreConsentEvents, identifyUser, resetUser, trackPage } from '@/lib/analytics/track';
import appLogger from '@/utils/logger';
import { scrubAuthTokensFromUrl } from '@/utils/authUrlScrub';

const GA4_ID = process.env.NEXT_PUBLIC_GA4_ID || 'G-C7J7ZMWSTV';
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID || 'GTM-WMV2CG37';
const isPostHogEnabled = process.env.NEXT_PUBLIC_POSTHOG_ENABLED !== 'false';

/**
 * Single root-level owner of the three trackers (PostHog, Datadog RUM consent,
 * Google Analytics) plus the cookie banner, identity, and SPA pageviews.
 *
 * Why one provider at the root instead of the previous split:
 *   - PostHog used to init only inside app/(private)/layout, so every marketing /
 *     auth page `usePostHog().capture(...)` was a silent no-op — the entire
 *     top-of-funnel (landing → pricing → signup) was untracked. Mounting here (the
 *     root layout, which wraps public + auth + private) makes the funnel continuous
 *     and gives one anonymous→identified PostHog identity across the whole journey.
 *   - GA4/GTM used to load only on public pages; hoisting them here makes
 *     `window.gtag('event', ...)` available everywhere so conversions in auth/app
 *     also reach GA.
 *   - Consent gates PostHog, GA4, and the "business events" layer (lib/analytics/
 *     track.ts's datadogRum.addAction/setUser calls): nothing initializes or sends
 *     until the visitor accepts; buffered first-touch events flush on accept. Datadog
 *     RUM/Logs base transport (errors, performance, session replay, appLogger calls)
 *     is NOT gated here -- it's strictly-necessary operational monitoring, granted
 *     unconditionally in DatadogInit.tsx. It was gated the same way as the other two
 *     until the fix in mb-server#4654's rollout, when it turned out the only UI that
 *     could ever grant it (the marketing-site cookie banner) can't reach the
 *     authenticated app (different origin, no shared localStorage) -- so RUM/Logs
 *     transport was silently dead for all real product usage for ~2 months.
 *
 * Must render inside AuthProvider/UserProvider/WorkspaceProvider (it reads them for
 * identify). It renders PostHogProvider so existing `usePostHog()` call sites keep
 * working on every surface.
 */
// `ready` is the consent flag. Including it in the deps means the pageview fires both
// on every route change AND the moment consent is granted — so the landing pageview
// that no-op'd pre-consent is captured without a navigation (fixes the missed-first-view gap).
function PageViewTracker({ ready }: { ready: boolean }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  useEffect(() => {
    // The HubSpot Marketplace install page carries a live OAuth code/state in
    // its query string — never emit pageviews (and their $current_url) for it.
    if (pathname?.startsWith(HUBSPOT_MARKETPLACE_INSTALL_PATH)) return;
    // trackPage is itself consent-gated + no-ops until PostHog/GA are live.
    trackPage();
  }, [pathname, searchParams, ready]);
  return null;
}

export default function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const { askIOTUserDetails } = useUserContext();
  const workspaceContext = useWorkspace();
  const identifiedRef = useRef<string | null>(null);
  const initedRef = useRef(false);

  // Consent mirrored into React state so the whole tree reacts to accept/decline.
  // Starts false on SSR AND first client render (localStorage is client-only), so the
  // conditionally-rendered GA scripts never cause a hydration mismatch; it then reflects
  // the real decision after mount and flips live when the visitor chooses.
  const [consentAccepted, setConsentAccepted] = useState(false);
  useEffect(() => {
    setConsentAccepted(hasAnalyticsConsent());
    return subscribeConsent((c) => setConsentAccepted(c === 'accepted'));
  }, []);

  // Single canonical identify path (the anonymous→user stitch). Consent-gated inside
  // identifyUser too. Re-created when identity inputs change, and invoked by the effect
  // below whenever those inputs OR consent change — so accepting consent after login
  // still identifies the already-logged-in user.
  const runIdentify = useCallback(() => {
    if (!hasAnalyticsConsent()) return;
    const userId = user?.id || askIOTUserDetails?.user_id;
    const ws = workspaceContext?.activeWorkspace;

    if (isAuthenticated && userId && askIOTUserDetails && ws) {
      if (identifiedRef.current === userId) return;
      identifyUser(userId, {
        email: user?.email,
        name:
          askIOTUserDetails.first_name && askIOTUserDetails.last_name
            ? `${askIOTUserDetails.first_name} ${askIOTUserDetails.last_name}`
            : undefined,
        user_role: askIOTUserDetails.role,
        plan_type: ws.subscriptionplan,
        workspace_id: ws.vendorid?.toString(),
        workspace_name: ws.workspace_name,
        signup_date: user?.created_at,
      });
      identifiedRef.current = userId;
      appLogger.info('analytics_user_identified', { userId, workspaceId: ws.vendorid });
    } else if (!isAuthenticated && identifiedRef.current) {
      resetUser();
      identifiedRef.current = null;
    }
  }, [isAuthenticated, user, askIOTUserDetails, workspaceContext?.activeWorkspace]);

  // Bring up / tear down providers as consent flips. Datadog RUM/Logs transport is no
  // longer part of this (DatadogInit.tsx always grants it -- strictly-necessary
  // operational monitoring, not revocable by decline); this effect now only owns
  // PostHog init and flushing the pre-consent business-event buffer.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!consentAccepted) return;

    // Consent granted: init PostHog once, flush buffered business events.
    if (isPostHogEnabled && !initedRef.current && !posthog.__loaded) {
      initedRef.current = true;
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY || '', {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
        capture_pageview: false, // we drive pageviews via PageViewTracker
        capture_pageleave: true,
        persistence: 'localStorage',
        enable_heatmaps: false, // avoids iframe recursion with the chatbot embed
        // Autocapture and pageleave stamp $current_url from the address bar.
        before_send: (event) => {
          const currentUrl = event?.properties?.$current_url;
          if (event && typeof currentUrl === 'string') {
            event.properties.$current_url = scrubAuthTokensFromUrl(currentUrl);
          }
          return event;
        },
      });
    }
    flushPreConsentEvents();
  }, [consentAccepted]);

  // Identify on identity change OR consent flip (the latter covers accept-after-login).
  useEffect(() => {
    runIdentify();
  }, [runIdentify, consentAccepted]);

  return (
    <PostHogProvider client={posthog}>
      {children}

      {/* The cookie-consent banner is intentionally NOT rendered here. This provider mounts at the
          root layout (public + auth + private), and we do not show a consent prompt on the auth or
          authenticated app surfaces. The banner lives in PublicTrackingScripts (public pages only);
          it writes to the shared consent store, which this provider subscribes to, so tracker gating
          still flips reactively when a public visitor accepts. */}

      {/* SPA pageviews — Suspense required by useSearchParams. `ready` makes the first
          (pre-consent) pageview fire the moment consent is granted. */}
      <Suspense fallback={null}>
        <PageViewTracker ready={consentAccepted} />
      </Suspense>

      {/* GA4 + GTM — loaded everywhere, but only after consent (gtag config auto-fires a
          pageview on load, so loading it pre-consent would itself be a consent breach).
          Gated on React state (not a direct localStorage read) so it mounts reactively on
          accept and stays SSR-consistent. */}
      {consentAccepted && (
        <>
          <GoogleTagManager gtmId={GTM_ID} />
          <Script src={`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`} strategy='afterInteractive' />
          <Script id='ga4-init' strategy='afterInteractive'>
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA4_ID}');
            `}
          </Script>
        </>
      )}
    </PostHogProvider>
  );
}
